import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Fixed app timezone for all "today"/"now" calculations, so availability and
// dashboard dates reflect the org's own calendar day regardless of the
// timezone the viewer's browser happens to be in.
export const APP_TIMEZONE = 'Europe/Brussels';

export function appNow(): Dayjs {
  return dayjs().tz(APP_TIMEZONE);
}

// A timezone-independent epoch-ms timestamp for a wall-clock date + optional time-of-day.
// Forced to UTC (trailing 'Z') rather than left as local time — the app's date/time columns
// carry no timezone of their own (Postgres `date`/`time`), so treating them as UTC internally
// is an arbitrary but *consistent* choice, and it avoids an hour of drift across a DST
// transition that relying on the JS engine's local-time interpretation would introduce.
// `time` may be serialized with seconds ('HH:mm:ss', sometimes with fractional seconds, as
// PostgREST always returns Postgres `time` columns) — sliced to the first 5 characters so both
// forms parse the same way. Missing time defaults to midnight.
// To compare against this, convert "now" through the same function — see `appNowTimestamp`.
export function toWallClockTimestamp(dateStr?: string, timeStr?: string): number | undefined {
  if (!dateStr) return undefined;
  const hhmm = (timeStr || '00:00').slice(0, 5);
  const time = Date.parse(`${dateStr}T${hhmm}:00Z`);
  return Number.isNaN(time) ? undefined : time;
}

// The current org-local (Brussels) wall-clock moment, expressed through the same arbitrary
// UTC-treatment as `toWallClockTimestamp`, so it's directly comparable to booking/machine
// date+time timestamps computed via that function.
export function appNowTimestamp(): number {
  const now = appNow();
  return toWallClockTimestamp(now.format('YYYY-MM-DD'), now.format('HH:mm'))!;
}

// Inverse of `toWallClockTimestamp`: turns one of its epoch-ms values back into the
// date/time strings it was built from. Reads it back as UTC for the same reason
// `toWallClockTimestamp` writes it as UTC — the value carries no real timezone, this is
// just the consistent round-trip through the arbitrary UTC-treatment.
export function fromWallClockTimestamp(ms: number): { date: string; time: string } {
  const d = dayjs.utc(ms);
  return { date: d.format('YYYY-MM-DD'), time: d.format('HH:mm') };
}
